// Board › Setup: the show's one board record (157, decision C11).
//
// PORTED FROM THE OCTOBER HAND-OFF'S console-apps-more.jsx (BoardSetup). One
// record for this page and the kiosk's leaderboard piece: places, the window
// the board ranks, how a name is drawn, open places and when it resets are set
// here once, and the piece, the standalone board and a published bundle all
// read the same row. A save reaches a wall on the board's next refresh, not at
// a publish, which is what the design's receipt says.
//
// WHAT IS NOT DRAWN, AND WHY.
//   Version (Kiosk / In app / Both) and "Mark the player's own place". The in
//   app board is the game's own (FortNine), and whether this console sets it
//   up is decision C11's open question. Nothing here would save anywhere.
//   "Fed by" is drawn as text: there is one game, and no record of a choice.
//   "Ties: earlier first" is not drawn as the design words it, because it is
//   not what the board does: two players on one score keep a steady order by
//   who they are, so the board does not swap them on every refresh. The page
//   says that instead, and the question is in the report to Gabe.
//
// WHO. Every role reads it; a Viewer reads it and changes nothing (Gabe, 13
// Oct). An Admin, or an Editor for a client they hold, saves and resets: the
// map's `boardEdit`, tied to the service's guard on each by check.mjs.

import { NoticeBox, PageHead, Setting, StickyBar, Switch } from "@nova/desk-ui/parts";
import { FieldRow, SecLabel } from "@nova/desk-ui/kit";
import { Confirm, Loading } from "@nova/desk-ui/states";
import { useEffect, useState, type ReactNode } from "react";
import {
  BOARD_MAX_PLACES,
  BOARD_NAMES_LABEL,
  BOARD_RESET_LABEL,
  BOARD_WINDOW_LABEL,
  readBoard,
  resetBoard,
  saveBoard,
  type BoardDesk,
  type BoardNames,
  type BoardReset,
  type BoardSettings,
  type BoardWindow,
} from "../api/board.js";
import type { ApiOptions, Failure } from "../api/client.js";
import { KIOSK_ORIGIN } from "../build/thisBuild.js";
import type { SessionToken } from "../session/store.js";
import { isRideToSurviveActivation } from "../../../api/src/auth/otpTemplates.js";
import { useConsoleToast } from "./Chrome.js";
import { Refusal } from "./Refusal.js";
import { opens, type Reader } from "./roleAccess.js";
import { useShow } from "./Show.js";

export type BoardSetupProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly reader: Reader;
  readonly onSessionEnded: (detail: string) => void;
  readonly onEnrol: () => void;
  /** The Game's Leaderboard, where the reader may open it. */
  readonly onStandings?: (() => void) | undefined;
  /** Kiosk › Screens, where the token the standalone link needs is issued. */
  readonly onScreens?: (() => void) | undefined;
};

type Phase =
  | { readonly phase: "reading" }
  | { readonly phase: "ready"; readonly desk: BoardDesk }
  | { readonly phase: "failed"; readonly failure: Failure };

/** What the form holds: places as typed, so a half-typed number is not a refusal yet. */
type Draft = Omit<BoardSettings, "places"> & { readonly places: string };

const WINDOWS: readonly BoardWindow[] = ["allTime", "monthly", "weekly", "custom"];
const NAMES: readonly BoardNames[] = ["tag", "first"];
const RESETS: readonly BoardReset[] = ["never", "daily", "manual"];

const LEDE = "The leaderboard the game feeds, and where it is shown.";

export function BoardSetup({
  api,
  token,
  activationId,
  reader,
  onSessionEnded,
  onEnrol,
  onStandings,
  onScreens,
}: BoardSetupProps): ReactNode {
  const frame = useShow();
  const say = useConsoleToast();
  const [show, setShow] = useState<Phase>({ phase: "reading" });
  const [attempt, setAttempt] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ readonly kind: "good" | "bad"; readonly detail: string } | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const canEdit = opens(reader, "boardEdit");
  const clientId = frame.status === "ready" ? (frame.line?.client.client_id ?? null) : null;

  const take = (desk: BoardDesk) => {
    setDraft({ ...desk.settings, places: String(desk.settings.places) });
    setShow({ phase: "ready", desk });
  };

  useEffect(() => {
    if (frame.status !== "ready") return;
    if (clientId === null) {
      setShow({
        phase: "failed",
        failure: { kind: "refused", detail: "This sign-in is not told which client this show belongs to, so its board cannot be read." },
      });
      return;
    }
    let cancelled = false;
    setShow({ phase: "reading" });
    setNote(null);
    void (async () => {
      const result = await readBoard(api, token, { activationId, clientId });
      if (cancelled) return;
      if (!result.ok) {
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        setShow({ phase: "failed", failure: result });
        return;
      }
      take(result.value);
    })();
    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, clientId, onSessionEnded, frame.status, attempt]);

  if (frame.status === "failed") {
    return (
      <Refusal
        onRetry={frame.reload}
        what="Could not load the board"
        failure={frame.failure}
        role={reader.role}
        onEnrol={onEnrol}
        notCounted="The board's setup was not read, and nothing was saved, because the activation did not load."
      />
    );
  }
  if (show.phase === "failed") {
    return (
      <Refusal
        onRetry={() => setAttempt((n) => n + 1)}
        what="Could not load the board"
        failure={show.failure}
        role={reader.role}
        onEnrol={onEnrol}
        notCounted="Nothing was saved. The kiosk's screens keep the board they last read."
      />
    );
  }
  if (show.phase === "reading" || draft === null) {
    return (
      <div className="stack">
        <PageHead title="Board" lede="Reading the board…" uuid={activationId} />
        <Loading rows={6} cols={2} />
      </div>
    );
  }

  const desk = show.desk;
  const places = Number(draft.places);
  const placesOk = /^\d+$/.test(draft.places.trim()) && places >= 1 && places <= BOARD_MAX_PLACES;
  const customOk = draft.window !== "custom" || draft.from !== null || draft.until !== null;
  const orderOk = draft.from === null || draft.until === null || draft.from <= draft.until;
  const settings: BoardSettings = { ...draft, places: placesOk ? places : desk.settings.places };
  const dirty = JSON.stringify(settings) !== JSON.stringify(desk.settings) || !placesOk;
  const problem = !placesOk
    ? `Places is a whole number from 1 to ${BOARD_MAX_PLACES}.`
    : !customOk
      ? "Custom dates need a From or an Until."
      : !orderOk
        ? "From is after Until, so the window holds no day."
        : null;
  const edit = (patch: Partial<Draft>) => {
    setNote(null);
    setDraft({ ...draft, ...patch });
  };

  const save = async () => {
    if (clientId === null || problem !== null) return;
    setBusy(true);
    setNote(null);
    const result = await saveBoard(api, token, { activationId, clientId, settings });
    setBusy(false);
    if (!result.ok) {
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      setNote({ kind: "bad", detail: result.detail });
      return;
    }
    take(result.value);
    say("Saved. Each board picks it up on its next refresh.");
  };

  const reset = async () => {
    setConfirmReset(false);
    if (clientId === null) return;
    setBusy(true);
    setNote(null);
    const result = await resetBoard(api, token, { activationId, clientId });
    setBusy(false);
    if (!result.ok) {
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      setNote({ kind: "bad", detail: result.detail });
      return;
    }
    take(result.value);
    say("The board starts again from nobody. Scores stay on the game's records.");
  };

  const ride = isRideToSurviveActivation(activationId.toLowerCase());
  const standalone = KIOSK_ORIGIN === null ? null : `${KIOSK_ORIGIN.replace(/\/+$/, "")}/board/${activationId}`;
  // Reset now works on what is saved: a board saved as never resetting has
  // nothing to reset, and the service refuses it in those words.
  const canReset = canEdit && desk.saved && desk.settings.reset !== "never" && !dirty;

  return (
    <div className="stack">
      <PageHead title="Board" lede={LEDE} uuid={activationId} />

      {!canEdit ? (
        <NoticeBox kind="quiet" title="You can read this board's setup">
          <p>An Admin, or an Editor for this client, changes it.</p>
        </NoticeBox>
      ) : !desk.saved ? (
        <NoticeBox kind="quiet" title="This board has not been set up">
          <p>
            Until it is saved, every board draws ten places of all-time scores by tag, as it always has. Save to set
            it for this show: the kiosk's leaderboard pieces and the standalone board then read what is here.
          </p>
        </NoticeBox>
      ) : null}

      <section>
        <SecLabel>Where it shows</SecLabel>
        <div className="panel panel-stack">
          <Setting label="Fed by" desc="The game whose scores the board ranks.">
            {ride ? <span>Game · Ride to Survive</span> : <span className="quiet">No game feeds this show yet</span>}
          </Setting>
          <Setting
            label="Kiosk pieces"
            desc="Every leaderboard piece in this show's rotation draws this board. A piece keeps only its heading and its time on the screen."
          >
            <span className="quiet">This board</span>
          </Setting>
        </div>
      </section>

      <section>
        <SecLabel>What it shows</SecLabel>
        <div className="panel panel-stack">
          <Setting label="Places" desc={`How many places a board draws, up to ${BOARD_MAX_PLACES}.`}>
            <input
              className="input num"
              type="number"
              min={1}
              max={BOARD_MAX_PLACES}
              aria-label="Places"
              value={draft.places}
              disabled={!canEdit}
              onChange={(event) => edit({ places: event.target.value })}
            />
          </Setting>
          <Setting label="Scores from" desc="The stretch of verified scores the board ranks. Custom dates are inclusive.">
            <select
              className="input"
              aria-label="Scores from"
              value={draft.window}
              disabled={!canEdit}
              onChange={(event) => {
                const next = event.target.value as BoardWindow;
                edit(next === "custom" ? { window: next } : { window: next, from: null, until: null });
              }}
            >
              {WINDOWS.map((option) => (
                <option key={option} value={option}>
                  {BOARD_WINDOW_LABEL[option]}
                </option>
              ))}
            </select>
          </Setting>
          {draft.window === "custom" ? (
            <div className="pair-wide">
              <FieldRow label="From">
                <input
                  className="input"
                  type="date"
                  value={draft.from ?? ""}
                  disabled={!canEdit}
                  onChange={(event) => edit({ from: event.target.value === "" ? null : event.target.value })}
                />
              </FieldRow>
              <FieldRow label="Until">
                <input
                  className="input"
                  type="date"
                  value={draft.until ?? ""}
                  disabled={!canEdit}
                  onChange={(event) => edit({ until: event.target.value === "" ? null : event.target.value })}
                />
              </FieldRow>
            </div>
          ) : null}
          <Setting label="A player's name" desc="How a name appears on a public screen. A surname is never shown.">
            <select
              className="input"
              aria-label="A player's name"
              value={draft.names}
              disabled={!canEdit}
              onChange={(event) => edit({ names: event.target.value as BoardNames })}
            >
              {NAMES.map((option) => (
                <option key={option} value={option}>
                  {BOARD_NAMES_LABEL[option]}
                </option>
              ))}
            </select>
          </Setting>
          <Setting label="Ties" desc="Two players on the same score keep the same order, so the board does not swap them on every refresh.">
            <span className="quiet">A steady order</span>
          </Setting>
          <Setting label="Open places" desc="Show the empty places below the last score.">
            <Switch
              on={draft.openPlaces}
              onFlip={() => {
                if (canEdit) edit({ openPlaces: !draft.openPlaces });
              }}
              label={draft.openPlaces ? "Shown" : "Hidden"}
            />
          </Setting>
        </div>
      </section>

      <section>
        <SecLabel>Resetting</SecLabel>
        <div className="panel panel-stack">
          <Setting label="Clear the board" desc="Scores are kept on the game's records whatever the board shows.">
            <select
              className="input"
              aria-label="Clear the board"
              value={draft.reset}
              disabled={!canEdit}
              onChange={(event) => edit({ reset: event.target.value as BoardReset })}
            >
              {RESETS.map((option) => (
                <option key={option} value={option}>
                  {BOARD_RESET_LABEL[option]}
                </option>
              ))}
            </select>
          </Setting>
          {desk.since !== null ? (
            <p className="help help-tight">Counting scores from {when(desk.since)}.</p>
          ) : null}
          <div className="acts acts-flush">
            {canEdit ? (
              <button type="button" className="btn btn-quiet" disabled={!canReset || busy} onClick={() => setConfirmReset(true)}>
                Reset now
              </button>
            ) : null}
            {onStandings !== undefined ? (
              <button type="button" className="btn btn-quiet" onClick={onStandings}>
                See the standings <span className="arw">→</span>
              </button>
            ) : null}
          </div>
          {canEdit && !canReset && desk.settings.reset === "never" ? (
            <p className="help help-tight">Reset now is for a board that clears. Choose when it clears and save first.</p>
          ) : null}
        </div>
      </section>

      <section>
        <SecLabel>Screens</SecLabel>
        <div className="panel panel-stack">
          <Setting
            label="Kiosk link"
            desc="Open on the wall screen to show only this board. Add #token= and the screen token from Kiosk · Screens to the end."
          >
            {standalone === null ? (
              <span className="quiet">This build names no kiosk</span>
            ) : (
              <span className="mono quiet">{standalone}</span>
            )}
          </Setting>
          {onScreens !== undefined ? (
            <div className="acts acts-flush">
              <button type="button" className="btn btn-quiet" onClick={onScreens}>
                Kiosk screens <span className="arw">→</span>
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {note !== null ? (
        <NoticeBox kind={note.kind === "good" ? "good" : "bad"} title={note.kind === "good" ? "Saved" : "Not saved"} alert={note.kind === "bad"}>
          <p>{note.detail}</p>
        </NoticeBox>
      ) : null}

      {canEdit ? (
        <StickyBar {...(problem !== null && dirty ? { warn: problem } : {})}>
          <button type="button" className="btn btn-primary" disabled={!dirty || problem !== null || busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save"}
          </button>
        </StickyBar>
      ) : null}

      {confirmReset ? (
        <Confirm title="Reset the board?" verb="Reset now" onCancel={() => setConfirmReset(false)} onConfirm={() => void reset()}>
          <p>The board starts again from nobody. Scores stay on the game's records.</p>
        </Confirm>
      ) : null}
    </div>
  );
}

function when(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
